import { useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@/primitives';
import { FilterInput } from '../../constants';
import type { BasicSearchField } from '../constants';
import { lookupOptionLabel } from '../constants';
import { lookupsWithI6Mapping } from '../expandI6LookupFilters';

interface Props {
  field: BasicSearchField;
  filtersOverride: FilterInput[];
  setFiltersOverride: (filters: FilterInput[]) => void;
}

export default function BasicSearchLookupCondition({
  field,
  filtersOverride,
  setFiltersOverride,
}: Props) {
  // Hide options that cannot expand (ILRX-4434 silent no-op).
  const lookups = lookupsWithI6Mapping(field.lookups);

  const lookupFieldKey = field.key ?? field.value;

  const selectedCode = useMemo(() => {
    const match = filtersOverride.find(
      (f) => f.field === `__lookup__${lookupFieldKey}`,
    );
    return match?.value ?? '';
  }, [filtersOverride, lookupFieldKey]);

  function handleChange(code: string) {
    const markerField = `__lookup__${lookupFieldKey}`;

    let next = filtersOverride.filter((f) => f.field !== markerField);

    if (code) {
      next = [...next, { field: markerField, operator: 'LOOKUP', value: code }];
    }

    setFiltersOverride(next);
  }

  function renderValue(value: unknown) {
    if (!value || value === '') {
      return (
        <Typography
          variant="titlemedium"
          sx={{ color: 'inherit', opacity: 0.5 }}
        >
          Select value
        </Typography>
      );
    }
    const found = lookups.find((l) => l.code === value);
    return (
      <Typography variant="titlemedium">
        {found ? lookupOptionLabel(found) : (value as string)}
      </Typography>
    );
  }

  return (
    <Stack
      sx={{
        flexDirection: { md: 'row' },
        gap: { xs: '6px', md: '12px' },
        mb: { xs: '16px', md: '16px' },
        height: { md: '56px' },
      }}
    >
      <FormControl sx={{ flex: 1 }}>
        <InputLabel shrink id={`bslc-label-${field.value}`}>
          {field.label}
        </InputLabel>
        <Select
          displayEmpty
          label={field.label}
          labelId={`bslc-label-${field.value}`}
          notched
          value={selectedCode}
          renderValue={renderValue}
          onChange={(e) => handleChange(e.target.value as string)}
          sx={{
            flex: 1,
            '.MuiSelect-select': { px: '12px' },
            fieldset: {
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'grey.500',
              borderRadius: '8px',
            },
            svg: {
              right: '10px',
              color: 'text.primary',
            },
          }}
        >
          <MenuItem sx={{ height: '36px' }} value="" />
          {lookups.map((lookup) => (
            <MenuItem key={lookup.code} value={lookup.code}>
              <Typography variant="titlemedium">
                {lookupOptionLabel(lookup)}
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );
}
